/**
 * TenantAccessLinks — exibe: (1) credenciais do admin da empresa com
 * ação "Resetar senha e mostrar nova"; (2) link único do Site da empresa.
 *
 * Removidos: Galeria pública, cards duplicados (admin/staff/portal/booking).
 * Usado em MasterTenantCreateWizard, MasterTenants, TenantSettings (aba Acessos).
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Copy, Share2, KeyRound, Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export function buildAccessLinks({ slug }) {
  const origin = (typeof window !== "undefined" ? window.location.origin : "");
  return [
    {
      key: "preview",
      label: "🌐 Site da empresa (link único)",
      url: `${origin}/empresa/${slug}`,
      sub: "Compartilhe SÓ este link. O próprio site direciona o cliente para agendar, entrar como cliente ou acessar a área da equipe.",
      color: "border-[hsl(var(--tenant-primary)/0.4)] bg-[hsl(var(--tenant-primary)/0.06)] dark:bg-[hsl(var(--tenant-primary)/0.12)]",
    },
  ];
}

const copy = (txt, label = "Link") =>
  navigator.clipboard.writeText(txt).then(
    () => toast.success(`${label} copiado!`),
    () => toast.error("Falha ao copiar."),
  );

/** Admin credentials block — apenas Master vê o botão de reset. */
function AdminCredsBlock({ tenantId, adminEmail, canReset }) {
  const [temp, setTemp] = useState(null);
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const reset = async () => {
    if (!confirm("Isso gera uma NOVA senha para o admin desta empresa. A anterior deixa de funcionar. Continuar?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/master/tenants/${tenantId}/reset-admin-password`);
      setTemp(data.temp_password);
      setShowPwd(true);
      toast.success("Nova senha gerada — copie e envie ao administrador.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 p-3 space-y-2" data-testid="tenant-access-admin-block">
      <div className="font-semibold text-sm">🔑 Acesso do Administrador da Empresa</div>
      <div className="text-xs text-muted-foreground">Login com maior privilégio dentro dessa empresa.</div>
      <div className="grid gap-2 mt-1">
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider w-20 text-muted-foreground">E-mail</span>
          <code className="flex-1 px-2 py-1 rounded bg-background text-xs font-mono truncate">{adminEmail || "—"}</code>
          {adminEmail && (
            <Button size="sm" variant="outline" onClick={() => copy(adminEmail, "E-mail")} data-testid="admin-email-copy">
              <Copy className="w-3 h-3" />
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider w-20 text-muted-foreground">Senha</span>
          <code className="flex-1 px-2 py-1 rounded bg-background text-xs font-mono">
            {temp ? (showPwd ? temp : "•".repeat(temp.length)) : (
              <span className="text-muted-foreground italic">— senha atual só o admin sabe —</span>
            )}
          </code>
          {temp && (
            <>
              <Button size="sm" variant="outline" onClick={() => setShowPwd(!showPwd)} data-testid="admin-pwd-toggle">
                {showPwd ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              </Button>
              <Button size="sm" variant="outline" onClick={() => copy(temp, "Senha")} data-testid="admin-pwd-copy">
                <Copy className="w-3 h-3" />
              </Button>
            </>
          )}
        </div>
        {canReset && (
          <Button size="sm" variant="outline" onClick={reset} disabled={busy} data-testid="admin-pwd-reset" className="w-fit">
            {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <KeyRound className="w-3 h-3 mr-1.5" />}
            {temp ? "Gerar nova senha" : "Resetar senha e mostrar"}
          </Button>
        )}
      </div>
    </div>
  );
}

export default function TenantAccessLinks({ slug, admin_email, legalName, intro, tenantId, canResetAdmin = false }) {
  const links = buildAccessLinks({ slug });

  const allText = [
    legalName ? `🏢 ${legalName} — Acessos` : "🏢 Acessos da empresa",
    "",
    ...links.flatMap((r) => [`${r.label}: ${r.url}`, r.sub ? `   ${r.sub}` : "", ""]),
  ].join("\n");

  const waText = encodeURIComponent(
    (legalName ? `*${legalName}* — Seus acessos:\n\n` : "Seus acessos:\n\n") +
    links.map((r) => `${r.label}\n${r.url}`).join("\n\n")
  );

  return (
    <div className="space-y-3" data-testid="tenant-access-links">
      {intro && <p className="text-sm text-muted-foreground">{intro}</p>}

      {/* Credenciais do administrador da empresa */}
      {tenantId && <AdminCredsBlock tenantId={tenantId} adminEmail={admin_email} canReset={canResetAdmin} />}

      {/* Link único de acesso público */}
      {links.map((row) => (
        <div key={row.key} className={`rounded-lg border-2 ${row.color} p-3`}>
          <div className="font-semibold text-sm">{row.label}</div>
          {row.sub && <div className="text-xs text-muted-foreground mt-0.5 mb-2">{row.sub}</div>}
          <div className="flex items-center gap-2 mt-1">
            <code className="flex-1 px-2 py-1 rounded bg-background text-xs font-mono truncate">{row.url}</code>
            <Button size="sm" variant="outline" onClick={() => copy(row.url, row.label)}>Copiar</Button>
            <Button size="sm" variant="outline" onClick={() => window.open(row.url, "_blank")}>Abrir</Button>
          </div>
        </div>
      ))}

      <div className="flex items-center justify-end gap-2 pt-2 border-t">
        <Button variant="outline" onClick={() => copy(allText, "Todos os acessos")}>
          <Copy className="w-3.5 h-3.5 mr-1.5" /> Copiar todos os links
        </Button>
        <Button variant="outline" onClick={() => window.open(`https://wa.me/?text=${waText}`, "_blank")}>
          <Share2 className="w-3.5 h-3.5 mr-1.5" /> Compartilhar no WhatsApp
        </Button>
      </div>
    </div>
  );
}
